import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Github, Linkedin, Mail, ChevronDown } from 'lucide-react';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Styled components
const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 0 1.5rem;
`;

const BackgroundGradient = styled.div`
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    ${({ theme }) => `${theme.colors.accent}20`} 0%,
    transparent 70%
  );
  z-index: -1;
  opacity: 0.8;
  filter: blur(40px);
`;

const BackgroundGradient2 = styled.div`
  position: absolute;
  bottom: -20%;
  left: -20%;
  width: 80%;
  height: 80%;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    ${({ theme }) => `${theme.colors.secondary}30`} 0%,
    transparent 70%
  );
  z-index: -1;
  opacity: 0.6;
  filter: blur(60px);
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: 4rem;
  
  @media (min-width: 992px) {
    grid-template-columns: 3fr 2fr;
    align-items: center;
  }
`;

const HeroContent = styled.div`
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const CodeBlock = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  padding: 1.5rem;
  position: relative;
  animation: ${float} 6s ease-in-out infinite;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  width: 100%;
  overflow: hidden;
  
  @media (max-width: 992px) {
    margin-top: 2rem;
  }
`;

const CodeHeader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: ${({ theme }) => theme.colors.card};
  border-radius: 12px 12px 0 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.secondary};
  display: flex;
  align-items: center;
  padding-left: 15px;
`;

const CodeDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  
  &.red { background-color: #f87171; }
  &.yellow { background-color: #fbbf24; }
  &.green { background-color: #34d399; }
`;

const CodeContent = styled.pre`
  margin-top: 20px;
  overflow-x: auto;
  color: ${({ theme }) => theme.colors.text};
  font-family: monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const Greeting = styled.span`
  display: inline-block;
  font-size: 1.25rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  background: linear-gradient(90deg, 
    ${({ theme }) => `${theme.colors.accent}40`}, 
    ${({ theme }) => `${theme.colors.accent}10`}
  );
  border-radius: 50px;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.accent};
  animation: ${fadeIn} 0.6s ease-out forwards;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  
  span {
    display: block;
  }
  
  .highlight {
    background: linear-gradient(
      90deg, 
      ${({ theme }) => theme.colors.accent}, 
      ${({ theme }) => `${theme.colors.accent}80`}
    );
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    background-size: 200% auto;
    animation: ${gradientAnimation} 4s linear infinite;
  }
`;

const SubTitle = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2.5rem;
  max-width: 600px;
  
  @media (max-width: 480px) {
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 3rem;
`;

const SocialButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.card};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    background: ${({ theme }) => theme.colors.accent};
    color: white;
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  
  span {
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  
  svg {
    animation: ${float} 2s ease-in-out infinite;
  }
`;

// Code example as plain string
const codeExample = `class Developer {
  constructor() {
    this.name = "Pascal Dietrich";
    this.skills = [
      "C++",
      "Python",
      "C#",
      "JavaScript"
    ];
    this.passions = [
      "Clean Code",
      "Test-Driven Development",
      "Continuous Learning"
    ];
  }

  createSolution() {
    return new Solution({
      robust: true,
      maintainable: true,
      followsBestPractices: true
    });
  }
}`;

const RedesignedHero = () => {
  const scrollToNextSection = () => {
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroSection id="hero">
      <BackgroundGradient />
      <BackgroundGradient2 />
      
      <ContentWrapper>
        <HeroContent>
          <Greeting>Hello, I'm</Greeting>
          <Title>
            Pascal Dietrich
            <span className="highlight">Software Developer</span>
          </Title>
          <SubTitle>
            As a passionate software developer, I prioritize clean code and test-driven development. 
            My goal is to create robust and maintainable software solutions that follow modern 
            development practices and industry best practices.
          </SubTitle>
          
          <SocialLinks>
            <SocialButton 
              href="https://github.com/pascalnd" 
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub Profile"
            >
              <Github size={20} />
            </SocialButton>
            <SocialButton
              href="https://linkedin.com/in/pascalnd"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn Profile"
            >
              <Linkedin size={20} />
            </SocialButton>
            <SocialButton
              href="mailto:mail@pascalndietrich.de"
              aria-label="Send Email"
            >
              <Mail size={20} />
            </SocialButton>
          </SocialLinks>
        </HeroContent>
        
        <CodeBlock>
          <CodeHeader>
            <CodeDot className="red" />
            <CodeDot className="yellow" />
            <CodeDot className="green" />
          </CodeHeader>
          <CodeContent>
            {codeExample}
          </CodeContent>
        </CodeBlock>
      </ContentWrapper>
      
      <ScrollIndicator onClick={scrollToNextSection}>
        <span>Scroll Down</span>
        <ChevronDown size={20} />
      </ScrollIndicator>
    </HeroSection>
  );
};

export default RedesignedHero;